import cx from 'classnames';
import { Anchor, FormV2 } from 'components/ui';
import { UploadCloud01, XClose } from 'ethena-icons';
import { useToast } from 'lib/hooks';
import {
  ChangeEvent,
  cloneElement,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FileIcon } from '../FileIcon';
import css from './SingleFileUpload.module.scss';

export interface ISingleFileUploadProps {
  accept: string;
  children?: ReactElement;
  className?: string;
  errorMessage?: string;
  errorMessageClassName?: string;
  file?: File;
  isDisabled?: boolean;
  isInvalid?: boolean;
  label?: string;
  maxFileSizeInMB?: number;
  onChange: (file: File) => void;
  previewUrl?: string;
}

export const SingleFileUpload = ({
  accept,
  children,
  className,
  errorMessage,
  errorMessageClassName,
  file: passedFile,
  isDisabled,
  isInvalid,
  label = 'Select file...',
  maxFileSizeInMB,
  onChange,
  previewUrl,
}: ISingleFileUploadProps) => {
  const { message } = useToast();
  const inputRef = useRef<HTMLInputElement>();
  const [file, setFile] = useState<File>(passedFile);

  // The `passedFile` can change after this component has initially rendered,
  // for example when a file is loaded async (`undefined` -> `File`) or when an
  // upload is cancelled (`File` -> `undefined` or `File` -> original `File`).
  useEffect(() => {
    if (passedFile) {
      setFile(passedFile);
    } else {
      inputRef.current.value = '';
      setFile(undefined);
    }
  }, [passedFile]);

  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files[0];

    if (!file) {
      return;
    }

    if (maxFileSizeInMB && file.size > maxFileSizeInMB * 1024 * 1024) {
      message.error(
        `File size exceeds ${maxFileSizeInMB}MB. Please select a smaller file.`
      );
      handleRemove();
    } else {
      setFile(file);
      onChange(file);
    }
  };

  const handleRemove = () => {
    inputRef.current.value = '';
    setFile(undefined);
    onChange(undefined);
  };

  const renderInput = () => {
    if (children) {
      return cloneElement(children, {
        onClick: () => !isDisabled && inputRef.current?.click?.(),
      });
    }
    return file ? renderRemove() : renderSelect();
  };

  const renderRemove = () => {
    if (isDisabled) {
      return (
        <div className={css.input}>
          <div className={css.fileName}>
            <FileIcon className="mr-2" fileName={file.name} />
            {file.name}
          </div>
          {previewUrl && (
            <Anchor
              className="ml-2"
              isDisabled={false}
              href={previewUrl}
              target="_blank"
            >
              Preview
            </Anchor>
          )}
        </div>
      );
    } else {
      return (
        <button className={cx(css.input, css.removable)} onClick={handleRemove}>
          <span className="d-flex align-items-center">
            <FileIcon className="mr-2" fileName={file.name} />
            {file.name}
          </span>
          <XClose className={css.remove} height={16} width={16} />
        </button>
      );
    }
  };

  const renderSelect = () => (
    <button
      className={cx(css.input, {
        [css.empty]: !file,
        [css.invalid]: isInvalid,
      })}
      onClick={() => inputRef.current?.click?.()}
    >
      {label}
      <UploadCloud01 height={16} width={16} />
    </button>
  );

  return (
    <div
      className={cx(css.container, className, {
        [css.invalid]: isInvalid,
      })}
    >
      {renderInput()}
      <input
        accept={accept}
        hidden
        onChange={handleChange}
        ref={inputRef}
        type="file"
      />
      <FormV2.Control.Feedback className={errorMessageClassName} type="invalid">
        {errorMessage}
      </FormV2.Control.Feedback>
    </div>
  );
};
